import {
  CLIENT_ERROR_LIMITS, errorFingerprint, safeDiagnosticContext, safeDiagnosticRoute, safeDiagnosticSource, safeDiagnosticVersion,
  safeErrorDescription, type ClientErrorKind, type ClientErrorReport, type ClientPlatform,
} from "@bridewell/shared";
import { api, ApiError, NetworkError } from "./client";

/*
 * Error reports from the apps to `POST /api/client-errors`, which writes them to the server's error log (the contract is
 * `clientErrorReportSchema` in @bridewell/shared). Each platform creates one reporter at start-up and hands it the
 * failures of its own code: uncaught exceptions, unhandled rejections, screens that crashed, queries that broke.
 */

type ContextValue = string | number | boolean;

export interface ErrorReporterOptions {
  platform: ClientPlatform;
  /** Build or app version identifying the source release ("1.0.0 (5)", a commit hash). */
  appVersion: string;
  /** The screen right now as a route pattern (`/app/w/$weddingId/guests`): never a concrete path, which carries ids and tokens. */
  route?: () => string | undefined;
  /** A few facts sent with every report (OS version, installed as an app); never personal data. */
  context?: () => Record<string, ContextValue> | undefined;
}

export interface ErrorReporter {
  /**
   * Sends one report, unless the error is an expected outcome (`isReportableError`), the same error was already sent
   * this run, or the run has used its `CLIENT_ERROR_LIMITS.reportsPerSession`. Never throws, never reports itself.
   */
  report(error: unknown, kind: ClientErrorKind, extra?: Record<string, ContextValue>): Promise<void>;
}

/**
 * An API answer (the API logs its own failures, with the request id), a lost connection and a cancelled request are
 * outcomes the screens already handle; everything else is a bug worth a report.
 */
export function isReportableError(error: unknown): boolean {
  if (error instanceof ApiError || error instanceof NetworkError) return false;
  return (error as { name?: unknown } | null | undefined)?.name !== "AbortError";
}

export function createErrorReporter(options: ErrorReporterOptions): ErrorReporter {
  const L = CLIENT_ERROR_LIMITS;
  const seen = new Set<string>();
  let sent = 0;
  return {
    async report(error, kind, extra) {
      try {
        if (!isReportableError(error)) return;
        const description = safeErrorDescription(error);
        const route = options.route?.();
        const screen = route ? safeDiagnosticRoute(route) : undefined;
        const source = safeDiagnosticSource(error instanceof Error ? error.stack : undefined, options.platform);
        const fingerprint = errorFingerprint({ name: description.name, source });
        if (seen.has(fingerprint) || sent >= L.reportsPerSession) return;
        seen.add(fingerprint);
        sent += 1;
        const context = safeDiagnosticContext({ ...options.context?.(), ...extra });
        delete context.bundleLine;
        delete context.bundleColumn;
        Object.assign(context, source);
        const body: ClientErrorReport = {
          platform: options.platform,
          appVersion: safeDiagnosticVersion(options.appVersion),
          kind,
          ...description,
          ...(screen ? { route: screen } : {}),
          ...(Object.keys(context).length ? { context } : {}),
          at: new Date().toISOString(),
        };
        // `keepalive`: a report sent while the page is closing still goes out (browsers allow up to 64 KB).
        await api("/client-errors", { method: "POST", json: body, keepalive: true });
      } catch {
        // Reporting is best effort: a report that fails is dropped, never retried and never reported.
      }
    },
  };
}
